import { ReservationStatus } from "@common/enum";
import { SearchMode } from "@common/enum/SearchMode";
import { IOriginDestinationRequest, TripType } from "@common/models";
import Button from "@components/general/form/Button";
import CheckboxInput from "@components/general/form/CheckboxInput";
import DateRangeInput from "@components/general/form/DateRangeInput";
import RoutePicker from "@components/general/form/RoutePicker";
import { IViewport } from "@config/appConfig";
import { Culture } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Notification from "@service/Notification";
import Translate from "@service/Translate";
import { validationMessage } from "@utilize/Util";
import { FC, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

interface IFlightTripItem {
    index: number;
    trip: IOriginDestinationRequest;
    showRemoveIcon: boolean;
    modifibleByRoute?: boolean;
    modifibleByDate?: boolean;
    onFlightTypeChange?: (name: string, value: any) => void;
}

const FlightTripItem: FC<IFlightTripItem> = ({ index, showRemoveIcon, trip, modifibleByDate = true, modifibleByRoute = true, onFlightTypeChange }) => {
    const { tr } = Translate;
    const dispatch = useDispatch();
    const dateTime = Culture.getDateInstance();
    const { small } = Config.get<IViewport>("viewport");
    const { routeOptions, rangeOptions, availabilityRequest, searchMode, validationErrors, width } = useSelector((state: IAppState) => ({
        availabilityRequest: state.availabilityRequest,
        routeOptions: state.routeOptions,
        rangeOptions: state.rangeOptions,
        validationErrors: state.validationErrors,
        searchMode: state.searchMode,
        width: state.windowWidth
    }));
    const formErrorsMessages = {
        arrivalDate: {
            required: tr("err_record_required", "return_date"),
            outboundDateAfterInbound: tr("rsv_105")
        },
        departureDate: {
            required: tr("err_record_required", availabilityRequest.tripType === TripType.RoundTrip ? "departure_date" : "flight_date"),
            multicitySequence: tr("err_multicity_departure_date_sequence")
        },
        destinationCode: {
            required: tr("err_record_required", "destination")
        },
        originCode: {
            required: tr("err_record_required", "origin")
        }
    };

    const onSelectedTripChange = (name: string, value: any) => {
        if (availabilityRequest.currentOriginDestinations?.find(mod => mod.status === ReservationStatus.UN &&
            trip.selectedSegmentRph === mod.flightSegments[0].reservationRph))
            return Notification.error("err_selected_all_un_flights");
        else
            onChange(name, value);
    }

    const onChange = (name: string, value: any) => {
        trip[name as keyof IOriginDestinationRequest] = value;
        if (name === "originCode") {
            trip.destinationCode = "";
        }
        if (validationErrors[index])
            delete validationErrors[index][name];

        dispatch<IAppAction>({ type: AppAction.ValidationErrors, payload: { validationErrors } });
        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
    }

    const onRouteReverse = () => {
        const tmp = trip.destinationCode;
        trip.destinationCode = trip.originCode;
        trip.originCode = tmp;
        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
    }

    const removeTrip = () => {
        if (availabilityRequest.originDestionations)
            availabilityRequest.originDestionations.splice(index, 1);
        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
    }

    let errors = validationErrors[index] ? validationMessage(formErrorsMessages, validationErrors[index]) : {};
    if (availabilityRequest.tripType === TripType.MultiCity && index > 0)
        if (availabilityRequest.originDestionations?.length && trip.departureDate && (availabilityRequest.originDestionations[index - 1].departureDate || 0) > (trip.departureDate || 0))
            errors.departureDate = formErrorsMessages.departureDate.multicitySequence;
    const modified = searchMode === SearchMode.Modify && (availabilityRequest.originDestionations || []).length > 1 ? true : false;

    return (
        <Fragment>
            {
                modified ?
                    <div className="col-1-auto checkbox-section">
                        <CheckboxInput
                            name="selected"
                            value={trip.selected}
                            onChange={onSelectedTripChange}
                        />
                    </div> : ""
            }
            <div className="col-1-1">
                <div className="row flex spa-1 flight-trip-section">
                    <div className={`col-3-5 col-sm-1-1 mb-sm${availabilityRequest.tripType === TripType.RoundTrip ? " col-md-1-2" : ""}${searchMode === SearchMode.Create && availabilityRequest.tripType === TripType.MultiCity ? " mb" : ""}`}>
                        <RoutePicker
                            index={index}
                            originCode={trip.originCode!}
                            destinationCode={trip.destinationCode!}
                            originError={errors.originCode}
                            destinationError={errors.destinationCode}
                            options={routeOptions}
                            onChange={onChange}
                            onRouteReverse={onRouteReverse}
                            disabled={(modified && !trip.selected) || !modifibleByRoute}
                        />
                    </div>
                    <div className={`flex col-2-5 col-sm-1-1${availabilityRequest.tripType === TripType.RoundTrip ? " col-md-1-2" : ""}`}>
                        <DateRangeInput
                            index={index}
                            startRange={trip.departureDate as number}
                            endRange={trip.arrivalDate as number}
                            options={rangeOptions}
                            minDateValue={dateTime.getTime()}
                            error={errors.departureDate || errors.arrivalDate || ""}
                            tripType={availabilityRequest.tripType}
                            disabled={(modified && !trip.selected) || !modifibleByDate}
                            gridCount={width <= small ? 12 : 2}
                            onChange={onChange}
                            onFlightTypeChange={onFlightTypeChange}
                        />
                        {
                            showRemoveIcon ?
                                <Button
                                    className="remove-trip-btn"
                                    icon="icon-close"
                                    width="40px"
                                    height="40px"
                                    color="danger"
                                    rounded
                                    onClick={removeTrip}
                                /> : ""
                        }
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default FlightTripItem;